import { createOnnxSession } from "@instacover/helpers"
import * as Sentry from "@sentry/react"
import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router"
import { useEffect, useMemo } from "react"
import { z } from "zod"

import {
  getClaimDataOptions,
  getPhotosOptions,
  getSessionConfigOptions,
  getSessionDetailOptions,
  getVideosOptions,
} from "@/api/hooks"
import { MODEL_URL } from "@/api/hooks/useCarViewModelQuery"
import { DOCUMENTS_MODEL_URL } from "@/api/hooks/useDocumentModelQuery"
import { isNotFoundError } from "@/api/trpcErrors"
import { ErrorScreen } from "@/components/ErrorScreen"
import { preloadFlags } from "@/components/icons/FlagIcon"
import { SpinnerLayout } from "@/components/layouts/SpinnerLayout"
import { PermissionsLogger } from "@/components/PermissionsLogger"
import { isPermissionsDisabledRoute } from "@/constants/permissions"
import { queryKeys } from "@/constants/queryKeys"
import {
  shouldPreloadCarViewCategorizationModel,
  shouldPreloadDocumentCategorizationModel,
} from "@/helpers/model/modelUtils"
import { arePermissionsDisabledForSession } from "@/helpers/orderedSteps/uploadFlags"
import { isInSdk } from "@/helpers/sdk"
import { applyLanguage, preloadLocaleResources } from "@/locales/i18n"
import { readPersistedLanguage } from "@/locales/persistedLanguage"
import { LINKS } from "@/routing/links"
import { prefetchSessionRoutes } from "@/routing/prefetchSessionRoutes"
import { NotFound } from "@/sections/errors/NotFound"
import { findOrderedSteps } from "@/services/dexie"
import { loadServerDataToDexie } from "@/services/dexie/functions/loadServerDataToDexie"
import { queryClient } from "@/services/tanstackQuery/queryClient"
import { injectThemeVariables } from "@/styles/themes/injectThemeVariables"
import { resolveTheme, ThemeProvider } from "@/styles/themes/themes"
import { BrowserSupport, resolveCurrentBrowserSupport } from "@/utils/browserSupport"
import { hasDismissedBrowserWarning } from "@/utils/browserWarningTracker"
import { isIOSDevice } from "@/utils/camera/captureMethod"
import { logger } from "@/utils/monitoring/logging"
import { startFullSessionReplay } from "@/utils/monitoring/monitoring"

import { GeolocationProvider } from "../../helpers/GeolocationProvider"
import { ImagePickerProvider } from "../../helpers/hooks/useImagePicker"
import { SessionWrapper } from "../../sections/session/SessionWrapper"
import { clearBlobUrlCache } from "../../utils/useBlobUrl"

const pathParamsSchema = z.object({ sessionId: z.string() })

// Same routes the permission checker skips, minus the permissions guide
function isGeolocationWatchDisabledRoute(pathname: string, sessionId: string): boolean {
  return isPermissionsDisabledRoute(pathname, sessionId) && !pathname.startsWith(LINKS.permissionsGuide(sessionId))
}

export const Route = createFileRoute("/$sessionId/_session")({
  params: {
    parse: (rawParams) => pathParamsSchema.parse(rawParams),
  },
  validateSearch: z.object({
    captureOpen: z.boolean().optional(),
    backlink: z.string().optional(),
    key: z.number().optional(),
  }),
  loader: async ({ params, location }) => {
    const { sessionId } = params

    Sentry.setTag("sessionId", sessionId)

    const [sessionDetail, config, photos, videos, claimData] = await Promise.all([
      queryClient.ensureQueryData(getSessionDetailOptions(sessionId)),
      queryClient.ensureQueryData(getSessionConfigOptions(sessionId)),
      queryClient.ensureQueryData(getPhotosOptions(sessionId)),
      queryClient.ensureQueryData(getVideosOptions(sessionId)),
      queryClient.ensureQueryData(getClaimDataOptions(sessionId)),
    ])

    const isTerminalSession = sessionDetail.finished || sessionDetail.canceled || sessionDetail.expired
    if (!isTerminalSession) {
      // Record the whole session for customers with enabled replays
      if (sessionDetail.sentryReplaysEnabled) {
        startFullSessionReplay("customer")
      }
      // record every iOS session with enabled ImageCapture
      else if (sessionDetail.carSilhouetteEnabled && isIOSDevice() && !isInSdk()) {
        startFullSessionReplay("iosImageCapture")
      }
    }

    // Block desktops and unsupported browsers, except finished/canceled/expired sessions, the native SDK
    // and upload-only sessions (all capture steps have enableUpload=true), which never need the camera
    const isOnUnsupportedDeviceRoute = location.pathname.startsWith(LINKS.unsupportedDevice(sessionId))
    const isBrowserCheckSkipped =
      isTerminalSession ||
      isOnUnsupportedDeviceRoute ||
      isInSdk() ||
      arePermissionsDisabledForSession(config.orderedSteps)

    if (!isBrowserCheckSkipped) {
      const browserSupport = resolveCurrentBrowserSupport()
      const isBlocked =
        browserSupport === BrowserSupport.DESKTOP ||
        (browserSupport === BrowserSupport.UNSUPPORTED_BROWSER && !hasDismissedBrowserWarning(sessionId))

      if (isBlocked) {
        logger.info(`Blocking session ${sessionId}: ${browserSupport}`)
        throw redirect({ to: "/$sessionId/unsupported-device", params: { sessionId }, replace: true })
      }
    }

    // Locale — a language the user picked earlier wins, otherwise the first configured one is the default.
    const [defaultLanguage] = config.languages
    const persistedLanguage = readPersistedLanguage(config.customerId)
    const isPersistedLanguageOffered = persistedLanguage !== undefined && config.languages.includes(persistedLanguage)

    await applyLanguage(
      isPersistedLanguageOffered ? persistedLanguage : defaultLanguage,
      config.organizationHumanReadableId,
    )

    // pre-load languages, so switching between them works offline
    await Promise.all([
      preloadLocaleResources(config.languages, config.organizationHumanReadableId),
      preloadFlags(config.languages),
    ])

    // Prefetch car view model only when at least one VehiclePhoto step has viewsCheck enabled
    // Skipped on the block screen, the device will never run it
    if (!isOnUnsupportedDeviceRoute && shouldPreloadCarViewCategorizationModel({ steps: config.orderedSteps })) {
      const modelUrl = config.organizationModelUrl ?? MODEL_URL
      queryClient.prefetchQuery({
        queryKey: queryKeys.browserModel(modelUrl),
        queryFn: async () => {
          const loadStart = Date.now()
          const thresholdsUrl = modelUrl.replace(/\.onnx$/, ".quality_thresholds.json")
          const thresholdsPromise = fetch(thresholdsUrl)
            .then((r) => (r.ok ? (r.json() as Promise<Record<number, number>>) : null))
            .catch(() => null)

          const { session: onnxSession, Tensor } = await createOnnxSession(modelUrl)
          const loadTime = Date.now() - loadStart

          const warmUpStart = Date.now()
          const arr = new Array(299 * 299 * 3).fill(0)
          const tensor = new Tensor(Int32Array.from(arr), [1, 299, 299, 3])
          await onnxSession.run({ input: tensor })
          const warmUpTime = Date.now() - warmUpStart

          const thresholds = await thresholdsPromise

          return { model: onnxSession, loadTime, warmUpTime, thresholds }
        },
        staleTime: Infinity,
        gcTime: Infinity,
      })
    }

    // Prefetch document categorization model only when at least one DocumentPhoto step has documentValidityCheck enabled
    if (!isOnUnsupportedDeviceRoute && shouldPreloadDocumentCategorizationModel({ steps: config.orderedSteps })) {
      queryClient.prefetchQuery({
        queryKey: queryKeys.browserModel(DOCUMENTS_MODEL_URL),
        queryFn: async () => {
          const loadStart = Date.now()
          const { session: onnxSession, Tensor } = await createOnnxSession(DOCUMENTS_MODEL_URL)
          const loadTime = Date.now() - loadStart

          const warmUpStart = Date.now()
          const arr = new Array(299 * 299 * 3).fill(0)
          const tensor = new Tensor(Int32Array.from(arr), [1, 299, 299, 3])
          await onnxSession.run({ input: tensor })
          const warmUpTime = Date.now() - warmUpStart

          return { model: onnxSession, loadTime, warmUpTime }
        },
        staleTime: Infinity,
        gcTime: Infinity,
      })
    }

    await loadServerDataToDexie({ sessionId, detail: sessionDetail, config, photos, videos, claimData })

    // Mirror the customer theme into CSS variables for Tailwind
    injectThemeVariables(resolveTheme(config))

    // Redirect finished/canceled/expired sessions
    const isTerminalRoute = location.pathname.endsWith("/finished") || location.pathname.endsWith("/canceled")

    if (!isTerminalRoute) {
      if (sessionDetail.finished) {
        throw redirect({ to: "/$sessionId/finished", params: { sessionId } })
      }

      if (sessionDetail.canceled || sessionDetail.expired) {
        throw redirect({ to: "/$sessionId/canceled", params: { sessionId } })
      }
    }

    // prefetch all needed route chunks
    const localSteps = await findOrderedSteps(sessionId)
    await prefetchSessionRoutes(localSteps)

    return { config, sessionDetail }
  },
  pendingComponent: () => <SpinnerLayout hideLogo={true} />,
  errorComponent: ({ reset, error }) =>
    isNotFoundError(error) ? <NotFound error={error} /> : <ErrorScreen handleRetry={reset} error={error} />,
  component: SessionLayoutRoute,
})

function SessionLayoutRoute() {
  const { config } = Route.useLoaderData()
  const { sessionId } = Route.useParams()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  useEffect(() => clearBlobUrlCache, [sessionId])

  const permissionsDisabled = arePermissionsDisabledForSession(config.orderedSteps)

  const geolocationEnabled = useMemo(() => {
    if (permissionsDisabled) return false

    return !isGeolocationWatchDisabledRoute(pathname, sessionId)
  }, [pathname, permissionsDisabled, sessionId])

  const theme = resolveTheme(config)

  // Block screen only shows the layout, no models, uploader, permission checks or geolocation
  if (pathname.startsWith(LINKS.unsupportedDevice(sessionId))) {
    return (
      <ThemeProvider theme={theme}>
        <Outlet />
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <GeolocationProvider enabled={geolocationEnabled}>
        <ImagePickerProvider>
          <PermissionsLogger />

          <SessionWrapper
            sessionId={sessionId}
            organizationModelUrl={config.organizationModelUrl}
            sessionConfig={config}
            permissionsDisabled={permissionsDisabled}
          />
        </ImagePickerProvider>
      </GeolocationProvider>
    </ThemeProvider>
  )
}
